import { getApiUrl } from './api-url'
import { getClientLocale } from './client-locale'

export async function getAuthToken(): Promise<string | null> {
  if (typeof window === 'undefined') return null
  return localStorage.getItem('accessToken')
}

export async function refreshAuthToken(): Promise<string | null> {
  if (typeof window === 'undefined') return null
  
  const refreshToken = localStorage.getItem('refreshToken')
  if (!refreshToken) return null

  try {
    const response = await fetch(`${getApiUrl()}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    })

    if (!response.ok) {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('sessionToken')
      localStorage.removeItem('user')
      return null
    }

    const data = await response.json()
    localStorage.setItem('accessToken', data.accessToken)
    if (data.refreshToken) {
      localStorage.setItem('refreshToken', data.refreshToken)
    }
    return data.accessToken
  } catch (error) {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('sessionToken')
      localStorage.removeItem('user')
    return null
  }
}

export async function fetchWithAuth(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  let token = await getAuthToken()

  if (!token) {
    throw new Error('No authentication token')
  }

  const isFormData =
    typeof FormData !== 'undefined' && options.body instanceof FormData
  const headers = new Headers(options.headers || {})
  const language = getClientLocale()
  headers.set('Authorization', `Bearer ${token}`)
  if (language) {
    headers.set('x-language', language)
  }
  if (!isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const response = await fetch(url, {
    ...options,
    headers,
  })

  // If token expired, try to refresh
  if (response.status === 401) {
    const newToken = await refreshAuthToken()
    if (newToken) {
      const refreshedHeaders = new Headers(options.headers || {})
      refreshedHeaders.set('Authorization', `Bearer ${newToken}`)
      if (language) {
        refreshedHeaders.set('x-language', language)
      }
      if (!isFormData && !refreshedHeaders.has('Content-Type')) {
        refreshedHeaders.set('Content-Type', 'application/json')
      }
      return fetch(url, {
        ...options,
        headers: refreshedHeaders,
      })
    } else {
      // Clear invalid tokens
      if (typeof window !== 'undefined') {
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('sessionToken')
        localStorage.removeItem('user')
        
        // Only redirect if not already on login page to prevent redirect loops
        if (window.location.pathname !== '/login') {
          sessionStorage.setItem(
            'redirectAfterLogin',
            `${window.location.pathname}${window.location.search}`,
          )
          window.location.href = '/login'
        }
      }
      throw new Error('Authentication failed')
    }
  }

  return response
}

